// frontend/src/pages/Notifications/Notifications.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useOutletContext } from 'react-router-dom';
import io from 'socket.io-client';
import './Notifications.css';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [unreadCount, setUnreadCount] = useState(0);
  const { user } = useAuth();
  const [socket, setSocket] = useState(null);
  
  // Get the refresh function from Layout context
  const outletContext = useOutletContext();
  const refreshNotificationCount = outletContext?.refreshNotificationCount;

  useEffect(() => {
    // Initialize socket connection
    if (user) {
      const newSocket = io('');
      newSocket.on('connect', () => {
        console.log('Notifications socket connected');
        newSocket.emit('authenticate', user.userId || user._id);
      });

      // Listen for real-time notifications
      newSocket.on('notification', (notification) => {
        setNotifications(prev => [notification, ...prev]);
        setUnreadCount(prev => prev + 1);
      });

      setSocket(newSocket);

      return () => {
        newSocket.disconnect();
      };
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [user]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/notifications', {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: 1, limit: 50 }
      });
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/notifications/unread-count', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unreadCount);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  // Helper to sync badge with Layout
  const syncLayoutBadge = () => {
    if (refreshNotificationCount) {
      refreshNotificationCount();
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `/api/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNotifications(prev =>
        prev.map(n => n._id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // Sync with Layout badge
      syncLayoutBadge();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        '/api/notifications/mark-all-read',
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      
      // Sync with Layout badge
      syncLayoutBadge();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `/api/notifications/${notificationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const notification = notifications.find(n => n._id === notificationId);
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
        // Sync with Layout badge
        syncLayoutBadge();
      }

      setNotifications(prev => prev.filter(n => n._id !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const clearAll = async () => {
    if (window.confirm('Are you sure you want to clear all notifications?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(
          '/api/notifications/clear-all',
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setNotifications([]);
        setUnreadCount(0);
        
        // Sync with Layout badge
        syncLayoutBadge();
      } catch (error) {
        console.error('Error clearing notifications:', error);
      }
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'like_post':
        return '❤️';
      case 'comment_post':
        return '💬';
      case 'follow':
        return '👤';
      case 'mention':
        return '@';
      case 'community_invite':
        return '👥';
      case 'community_accepted':
        return '✅';
      case 'achievement_unlocked':
        return '🏆';
      case 'study_reminder':
        return '📚';
      case 'new_community_post':
        return '📝';
      default:
        return '🔔';
    }
  };

  const filteredNotifications = filter === 'all'
    ? notifications
    : notifications.filter(n => !n.read);

  const formatTimeAgo = (date) => {
    if (!date) return '';

    const now = new Date();
    const notifDate = new Date(date);
    const diffMs = now - notifDate;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 7) {
      return notifDate.toLocaleDateString();
    } else if (diffDays > 0) {
      return `${diffDays}d ago`;
    } else if (diffHours > 0) {
      return `${diffHours}h ago`;
    } else if (diffMins > 0) {
      return `${diffMins}m ago`;
    } else {
      return 'Just now';
    }
  };

  if (!user) {
    return (
      <div className="notifications-container">
        <div className="notifications-header">
          <h1>Notifications</h1>
        </div>
        <div className="no-notifications">
          <p>Please log in to view notifications</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="notifications-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="notifications-container">
      <div className="notifications-header">
        <h1>Notifications</h1>
        <div className="header-actions">
          {unreadCount > 0 && (
            <button onClick={markAllAsRead} className="mark-all-read-btn">
              Mark all as read
            </button>
          )}
          {notifications.length > 0 && (
            <button onClick={clearAll} className="clear-all-btn">
              Clear all
            </button>
          )}
        </div>
      </div>

      <div className="notifications-tabs">
        <button
          className={`tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All
        </button>
        <button
          className={`tab ${filter === 'unread' ? 'active' : ''}`}
          onClick={() => setFilter('unread')}
        >
          Unread {unreadCount > 0 && `(${unreadCount})`}
        </button>
      </div>

      <div className="notifications-list">
        {filteredNotifications.length === 0 ? (
          <div className="no-notifications">
            <p>
              {filter === 'unread'
                ? 'No unread notifications'
                : 'No notifications yet'}
            </p>
          </div>
        ) : (
          filteredNotifications.map(notification => (
            <div
              key={notification._id}
              className={`notification-item ${!notification.read ? 'unread' : ''}`}
              onClick={() => !notification.read && markAsRead(notification._id)}
            >
              <div className="notification-icon">
                {getNotificationIcon(notification.type)}
              </div>
              <div className="notification-content">
                <div className="notification-message">
                  {notification.message}
                </div>
                <div className="notification-time">
                  {notification.timeAgo || formatTimeAgo(notification.createdAt)}
                </div>
                {notification.additionalData && (
                  <div className="notification-extra">
                    {notification.additionalData.commentPreview && (
                      <div className="comment-preview">
                        "{notification.additionalData.commentPreview}"
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button
                className="notification-delete"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteNotification(notification._id);
                }}
              >
                ×
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Notifications;
